"use client";

import { CircularProgress } from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import UserForm from "@/components/templates/UserForm";
import {
  buildUserFormInitialData,
  buildUserFormSchema,
  normalizeUserFormData,
} from "@/components/templates/UserForm/form-data";
import { UserFormData } from "@/components/templates/UserForm/types";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useInserUserProfile from "@/hooks/requests/permissions/use-insert-user-profile";
import useRemoveUserProfile from "@/hooks/requests/permissions/use-remove-user-profile";
import useCreateUser from "@/hooks/requests/user/use-create-user";
import useGetUserById from "@/hooks/requests/user/use-get-user-by-id";
import useUpdateUser from "@/hooks/requests/user/use-update-user";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import { UsersMessages } from "@/messages/users/user.messages";
import { BreadCrumb } from "@/types/page.types";

type Props = {
  messages: UsersMessages;
};
export default function UserFormPage({ messages }: Props) {
  const { userId } = useParams();
  const isEditing = !!userId;
  const { user: authUser } = useAuth();
  const { data: user, isLoading: isLoadingUser, error } = useGetUserById();
  const { create, isLoading: isLoadingCreate } = useCreateUser();
  const { update, isLoading: isLoadingUpdate } = useUpdateUser();
  const { startLoading, endLoading, isLoading: isLoadingSubmit } = useLoading();
  const { insert: insertUserProfile } = useInserUserProfile();
  const { remove: removeUserProfile } = useRemoveUserProfile();
  const { mutate } = useMutate();
  const router = useNavigate();
  const { lang } = useLanguage();

  const onSuccess = () => {
    mutate(SWR_KEYS.getUserById);
    mutate(SWR_KEYS.getUsers);
    router(ROUTE_URLS.users(lang));

    endLoading();
  };

  const handleUpdateUser = async (formData: UserFormData) => {
    if (!user) return;

    startLoading();

    const existingProfiles = user.userProfile.map((p) => p.profileId);
    const updatedProfiles = formData.userProfile.map((p) => p.profileId);

    const profilesToAdd = updatedProfiles.filter((p) => !existingProfiles.includes(p));

    for (const profileId of profilesToAdd) {
      await insertUserProfile({
        formData: {
          userId: Number(user.id),
          profileId,
        },
        successMessage: messages.insertUserProfileSuccess,
      });
    }

    const profilesToRemove = existingProfiles.filter((p) => !updatedProfiles.includes(p));
    for (const profileId of profilesToRemove) {
      await removeUserProfile({
        _id: user.id,
        secondaryId: profileId,
        successMessage: messages.removeUserProfileSuccess,
      });
    }

    await update({
      _id: Number(user.id),
      formData: normalizeUserFormData(formData),
    });

    onSuccess();
  };

  const handleCreateUser = async (formData: UserFormData) => {
    const normalizedData = normalizeUserFormData(formData);

    startLoading();

    const result = (await create({
      formData: normalizedData,
      successMessage: messages.createUserSuccess,
      errorMessage: messages.createUserError,
    })) as { id?: number } | null;

    if (result?.id && formData.userProfile.length > 0) {
      const createdId: number = result.id;
      await Promise.all(
        formData.userProfile.map((profile) =>
          insertUserProfile({
            formData: {
              userId: createdId,
              profileId: profile.profileId,
            },
            // Suprimir mensagem de sucesso aqui para não sobrescrever a de criação
          })
        )
      );
    }

    onSuccess();
  };

  const handleSubmitForm = async (_formData: UserFormData) => {
    startLoading();

    if (isEditing) handleUpdateUser(_formData);
    if (!isEditing) handleCreateUser(_formData);

    endLoading();
  };

  const BREAD_CRUMBS: BreadCrumb[] = [
    { title: messages.userBreadCrumbTitle, href: ROUTE_URLS.users(lang) },
  ];

  const isSuspended = isEditing ? isLoadingUser : !authUser;
  const isLoading = isLoadingCreate || isLoadingUpdate || isLoadingSubmit || isLoadingUser;
  const pageTitle = isEditing ? messages.updateUserPageTitle : messages.createUserPageTitle;

  const shouldLoadUsersForm = isEditing ? !!user && !error : !!authUser;
  const shouldLoadError = !user && !!error && !isLoading;

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isSuspended}>
          {shouldLoadUsersForm && (
            <Formik
              initialValues={buildUserFormInitialData(isEditing ? user : null)}
              validationSchema={buildUserFormSchema(messages)}
              validateOnChange={false}
              onSubmit={handleSubmitForm}
            >
              <UserForm
                messages={messages}
                isLoading={isLoading}
                isEditing={isEditing}
                showPermission
              />
            </Formik>
          )}

          {!!shouldLoadError && (
            <AlertBlock
              title={messages.userErrorToLoad}
              description={error.message}
              alt={messages.userNotFoundAlt}
            />
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}
